/**
 * PostHog Client Wrapper
 *
 * Lazy-loads posthog-js on first user interaction or during browser idle time
 * to keep the library out of the entry chunk (~176KB uncompressed).
 *
 * Public API:
 *   capture(event, props)   — queues if not ready, otherwise fires immediately
 *   identify(id, props)     — queues if not ready
 *   reset()                 — queues if not ready
 *   getClient()             — returns the posthog instance once loaded, else null
 *   initPostHog()           — triggers the dynamic import + init (idempotent)
 *
 * If VITE_POSTHOG_KEY is unset, initPostHog() still resolves successfully but no
 * events are forwarded (queue is drained into a no-op). This keeps callers safe.
 */

const QUEUE_CAP = 50;

// Each entry: { kind: 'capture' | 'identify' | 'reset', args: [...], timestamp: number }
let queue = [];
let client = null;
let initPromise = null;
let initStarted = false;

/**
 * Push to the queue, dropping the oldest entry if we're over cap.
 */
function enqueue(entry) {
  if (queue.length >= QUEUE_CAP) {
    const dropped = queue.shift();
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[posthogClient] queue overflow, dropped oldest:', dropped);
    }
  }
  queue.push(entry);
}

/**
 * Flush queued events into the live posthog instance.
 * No-op client (null) just drops the queue.
 */
function flush() {
  const pending = queue;
  queue = [];
  if (!client) return;
  for (const entry of pending) {
    try {
      if (entry.kind === 'capture') {
        const [event, props] = entry.args;
        // Pass a timestamp override when possible so queued events reflect when
        // the user actually interacted, not when PostHog finished booting.
        client.capture(event, props, { timestamp: new Date(entry.timestamp) });
      } else if (entry.kind === 'identify') {
        client.identify(...entry.args);
      } else if (entry.kind === 'reset') {
        client.reset(...entry.args);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[posthogClient] flush error:', err);
    }
  }
}

/**
 * Capture an event. Safe to call before or after PostHog has loaded.
 */
export function capture(event, props) {
  if (client) {
    try {
      client.capture(event, props);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[posthogClient] capture error:', err);
    }
    return;
  }
  enqueue({ kind: 'capture', args: [event, props], timestamp: Date.now() });
}

/**
 * Identify a user. Queued if PostHog hasn't loaded yet.
 */
export function identify(id, props) {
  if (client) {
    try {
      client.identify(id, props);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[posthogClient] identify error:', err);
    }
    return;
  }
  enqueue({ kind: 'identify', args: [id, props], timestamp: Date.now() });
}

/**
 * Reset the identified user (e.g. logout).
 */
export function reset() {
  if (client) {
    try {
      client.reset();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[posthogClient] reset error:', err);
    }
    return;
  }
  enqueue({ kind: 'reset', args: [], timestamp: Date.now() });
}

/**
 * Returns the posthog instance once loaded, or null if not yet initialized or
 * if VITE_POSTHOG_KEY is missing.
 */
export function getClient() {
  return client;
}

/**
 * Dynamically import posthog-js and initialize it. Idempotent — safe to call
 * repeatedly from multiple trigger paths (idle callback + first-interaction).
 *
 * Returns a promise that resolves once init is complete (or immediately if
 * VITE_POSTHOG_KEY is unset).
 */
export function initPostHog() {
  if (initStarted) return initPromise;
  initStarted = true;

  const key = import.meta.env?.VITE_POSTHOG_KEY;
  if (!key) {
    // No key — drain the queue into the void so memory doesn't grow.
    initPromise = Promise.resolve().then(() => {
      queue = [];
    });
    return initPromise;
  }

  initPromise = import('posthog-js')
    .then((mod) => {
      const posthog = mod.default ?? mod;
      posthog.init(key, {
        api_host: 'https://us.i.posthog.com',
        ui_host: 'https://us.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: false, // We fire $pageview manually on SPA route changes
        capture_pageleave: true,
        disable_surveys: true,
        session_recording: {
          maskAllInputs: true,
        },
      });
      client = posthog;
      flush();
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[posthogClient] failed to load posthog-js:', err);
      // Drain the queue so we don't leak memory on a permanent load failure.
      queue = [];
    });

  return initPromise;
}
